import { useState } from "react";

interface StockIconProps {
  character: string;
  color?: string;
  size?: number;
  totalIcons?: number;
  activeIcons?: number;
  className?: string;
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/\./g, "")
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
}

// Maps any common character spelling (full name, slippi-js shortName, or popular
// nickname) to the icon filename slug under public/stock-icons/. Derived from
// slippi-js characters.getAllCharacters() plus a few extra community nicknames.
const characterToIconSlug: Record<string, string> = {
  "captain-falcon": "captain-falcon",
  falcon: "captain-falcon",
  cf: "captain-falcon",
  "donkey-kong": "donkey-kong",
  dk: "donkey-kong",
  fox: "fox",
  "mr-game-and-watch": "mr-game-and-watch",
  "game-and-watch": "mr-game-and-watch",
  gandw: "mr-game-and-watch",
  gnw: "mr-game-and-watch",
  kirby: "kirby",
  bowser: "bowser",
  link: "link",
  luigi: "luigi",
  mario: "mario",
  marth: "marth",
  mewtwo: "mewtwo",
  m2: "mewtwo",
  ness: "ness",
  peach: "peach",
  pikachu: "pikachu",
  pika: "pikachu",
  "ice-climbers": "ice-climbers",
  ics: "ice-climbers",
  icies: "ice-climbers",
  popo: "ice-climbers",
  nana: "ice-climbers",
  jigglypuff: "jigglypuff",
  puff: "jigglypuff",
  jiggs: "jigglypuff",
  samus: "samus",
  yoshi: "yoshi",
  zelda: "zelda",
  sheik: "sheik",
  falco: "falco",
  "young-link": "young-link",
  ylink: "young-link",
  yl: "young-link",
  "dr-mario": "doctor-mario",
  "doctor-mario": "doctor-mario",
  doc: "doctor-mario",
  roy: "roy",
  pichu: "pichu",
  ganondorf: "ganondorf",
  ganon: "ganondorf",
};

function characterSlug(character: string): string {
  const slug = slugify(character);
  return characterToIconSlug[slug] ?? slug;
}

function colorSlug(color: string): string {
  const slug = slugify(color);
  return slug === "default" || slug === "" ? "neutral" : slug;
}

const INACTIVE_FILTER = "brightness(0.6) saturate(0.5)";
const NATIVE_ICON_SIZE = 24;

function SingleIcon({
  character,
  color,
  size,
  active,
}: {
  character: string;
  color: string;
  size: number;
  active: boolean;
}) {
  const charSlug = characterSlug(character);
  const initialSrc = `/stock-icons/${colorSlug(color)}-${charSlug}.png`;
  const fallbackSrc = `/stock-icons/neutral-${charSlug}.png`;
  const [src, setSrc] = useState(initialSrc);

  // Nearest-neighbor only looks crisp at integer multiples of the native 24px source;
  // otherwise the browser produces chunky, irregular blocks. Fall back to smooth scaling
  // for non-integer-multiple sizes.
  const isIntegerMultiple = size % NATIVE_ICON_SIZE === 0;

  return (
    <img
      src={src}
      alt={character}
      title={character}
      width={size}
      height={size}
      style={{
        imageRendering: isIntegerMultiple ? "pixelated" : "auto",
        verticalAlign: "middle",
        filter: active ? undefined : INACTIVE_FILTER,
      }}
      onError={() => {
        if (src !== fallbackSrc) setSrc(fallbackSrc);
      }}
    />
  );
}

export function StockIcon({
  character,
  color = "Default",
  size = NATIVE_ICON_SIZE,
  totalIcons = 1,
  activeIcons,
  className,
}: StockIconProps) {
  const total = Math.max(0, totalIcons);
  const active = Math.max(0, Math.min(total, activeIcons ?? total));

  if (total === 1) {
    return (
      <span className={className}>
        <SingleIcon character={character} color={color} size={size} active={active === 1} />
      </span>
    );
  }

  return (
    <span className={`d-inline-flex align-items-center gap-1 ${className ?? ""}`}>
      {Array.from({ length: total }, (_, i) => (
        <SingleIcon key={i} character={character} color={color} size={size} active={i < active} />
      ))}
    </span>
  );
}

export interface CharacterColor {
  name: string;
  color?: string;
}

interface StockIconsProps {
  characters: ReadonlyArray<string | CharacterColor>;
  color?: string;
  size?: number;
  className?: string;
}

export function StockIcons({ characters, color, size, className }: StockIconsProps) {
  if (characters.length === 0) return null;
  return (
    <span className={`d-inline-flex align-items-center gap-1 ${className ?? ""}`}>
      {characters.map((entry, i) => {
        const name = typeof entry === "string" ? entry : entry.name;
        const entryColor = typeof entry === "string" ? color : (entry.color ?? color);
        return <StockIcon key={`${name}-${i}`} character={name} color={entryColor} size={size} />;
      })}
    </span>
  );
}
